import type { PropsWithChildren, ReactNode } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { generatePath } from '@/services/router/generate-path';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import PeopleListPage from './index.page';
import PeopleCreatePage from './create/index.page';
import PeopleNewPage from './new/index.page';
import PeopleMergePage from './merge/index.page';

const LOADING_LABEL = 'Loading…';

/**
 * The segment a migrated People URL was reached at. Both spellings ship; `people` is the
 * canonical one and `profiles` forwards to it.
 */
export type PeopleUrlSegment = 'people' | 'profiles';

/** The segment the migrated People URLs are served at. */
const CANONICAL_SEGMENT: PeopleUrlSegment = 'people';

/** The migrated People URL each key names. */
const PEOPLE_ROUTES = {
  'people.list': '/workspaces/:workspaceId/people',
  'people.create': '/workspaces/:workspaceId/people/create',
  'people.new': '/workspaces/:workspaceId/people/new',
  'people.merge': '/workspaces/:workspaceId/people/merge',
} as const;

/** The keys for the migrated People URLs. */
type PeopleRouteKey = keyof typeof PEOPLE_ROUTES;

type PeopleRouteViewProps = {
  segment: PeopleUrlSegment;
};

/** The migrated URL, with the query string carried across; `null` while the workspace resolves. */
function useMigratedPeopleHref(routeKey: PeopleRouteKey): string | null {
  const template = PEOPLE_ROUTES[routeKey];

  return useMigrationHandoffHref((workspaceId) => template.replace(':workspaceId', workspaceId));
}

type ReplaceNavigateProps = {
  /** Where to go; `null` while the workspace resolves. */
  href: string | null;
};

/** Replace-navigates to `href`, holding a loader until it resolves. */
function ReplaceNavigate({ href }: ReplaceNavigateProps) {
  if (href == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={href} options={{ shouldReplaceHistoryEntry: true }} />;
}

type JourneysHandoffProps = {
  /** The key for the migrated URL this one forwards to. */
  routeKey: PeopleRouteKey;
};

/** Shared body of the `journeys` handoffs below: a forward to the migrated URL. */
function JourneysHandoff({ routeKey }: JourneysHandoffProps) {
  const migratedHref = useMigratedPeopleHref(routeKey);

  return <ReplaceNavigate href={migratedHref} />;
}

type CanonicalSpellingHandoffProps = PropsWithChildren<{
  /** Whether the URL was reached at the canonical segment. */
  isCanonical: boolean;
  /** The canonical URL; `null` while the workspace resolves. */
  canonicalHref: string | null;
}>;

/**
 * Holds a migrated People page to its canonical spelling: the other spelling forwards to it,
 * replacing history, so the two never disagree in the address bar.
 */
function CanonicalSpellingHandoff({ isCanonical, canonicalHref, children }: CanonicalSpellingHandoffProps) {
  if (isCanonical) {
    return children;
  }

  return <ReplaceNavigate href={canonicalHref} />;
}

type PeopleRouteFrameProps = PropsWithChildren<{
  segment: PeopleUrlSegment;
  /** The key for this URL's canonical target. */
  routeKey: PeopleRouteKey;
}>;

/**
 * The shell of every People page: a loader until the current workspace resolves (the pages' reads
 * go through `requireWorkspaceId`, which throws while the workspaces list is still loading), then
 * the page at its canonical spelling.
 */
function PeopleRouteFrame({ segment, routeKey, children }: PeopleRouteFrameProps) {
  const canonicalHref = useMigratedPeopleHref(routeKey);
  const workspace = useCurrentWorkspace();

  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return (
    <CanonicalSpellingHandoff isCanonical={segment === CANONICAL_SEGMENT} canonicalHref={canonicalHref}>
      {children}
    </CanonicalSpellingHandoff>
  );
}

/** The old `/journeys/people` and `/journeys/profiles` URLs. */
export function JourneysPeopleHandoff() {
  return <JourneysHandoff routeKey="people.list" />;
}

/** The People list, served at `/people` and `/profiles`. */
export function PeopleListRouteView({ segment }: PeopleRouteViewProps) {
  return (
    <PeopleRouteFrame segment={segment} routeKey="people.list">
      <RumPageRendered>
        <PeopleListPage />
      </RumPageRendered>
    </PeopleRouteFrame>
  );
}

/**
 * The old `/journeys/people/create` and `/journeys/profiles/create` URLs, the
 * add-people twins of {@link JourneysPeopleHandoff}.
 */
export function JourneysPeopleCreateHandoff() {
  return <JourneysHandoff routeKey="people.create" />;
}

type PeopleChildRouteViewProps = PeopleRouteViewProps & {
  /** The key for this URL's canonical target. */
  routeKey: 'people.create' | 'people.new';
  children: ReactNode;
};

/**
 * The shell of a People child URL (`/people/create` and `/people/new`, and their `profiles`
 * spellings): the page, behind the `journeys/people/create` permission, which sends a denied
 * viewer to the People list.
 */
function PeopleChildRouteView({ segment, routeKey, children }: PeopleChildRouteViewProps) {
  const workspace = useCurrentWorkspace();

  return (
    <PeopleRouteFrame segment={segment} routeKey={routeKey}>
      {/*
        Redirect target is built from the resolved workspace rather than `ProtectedRoute`'s route
        params: `PeopleRouteFrame` already gated on that workspace, and the `last` sentinel is only
        a fallback for the window before it resolves.
      */}
      <ProtectedRoute
        permission="journeys/people/create"
        redirectTo={generatePath('/workspaces/:workspaceId/people', { workspaceId: workspace?.id ?? 'last' })}
      >
        {children}
      </ProtectedRoute>
    </PeopleRouteFrame>
  );
}

/** The add-people catalog, served at `/people/create` and `/profiles/create`. */
export function PeopleCreateRouteView({ segment }: PeopleRouteViewProps) {
  return (
    <PeopleChildRouteView segment={segment} routeKey="people.create">
      <RumPageRendered>
        <PeopleCreatePage />
      </RumPageRendered>
    </PeopleChildRouteView>
  );
}

/**
 * The old `/journeys/people/new` and `/journeys/profiles/new` URLs — the
 * add-a-person twins of {@link JourneysPeopleHandoff}. The add-people catalog
 * in the embedded app still links here.
 */
export function JourneysPeopleNewHandoff() {
  return <JourneysHandoff routeKey="people.new" />;
}

/** The add-a-person form, served at `/people/new` and `/profiles/new`. */
export function PeopleNewRouteView({ segment }: PeopleRouteViewProps) {
  return (
    <PeopleChildRouteView segment={segment} routeKey="people.new">
      <RumPageRendered>
        <PeopleNewPage />
      </RumPageRendered>
    </PeopleChildRouteView>
  );
}

/**
 * The old `/journeys/people/merge` and `/journeys/profiles/merge` URLs — the
 * merge-wizard twins of {@link JourneysPeopleHandoff}. The embedded app's person
 * page and activity log, and the copilot's suggestions, still link here.
 */
export function JourneysPeopleMergeHandoff() {
  return <JourneysHandoff routeKey="people.merge" />;
}

/**
 * The merge-two-people wizard, served at `/people/merge` and `/profiles/merge`. Unlike the
 * add-people pages there is no permission gate at the route: a viewer without
 * `journeys/people/edit` sees the wizard with its controls disabled.
 */
export function PeopleMergeRouteView({ segment }: PeopleRouteViewProps) {
  return (
    <PeopleRouteFrame segment={segment} routeKey="people.merge">
      <RumPageRendered>
        <PeopleMergePage />
      </RumPageRendered>
    </PeopleRouteFrame>
  );
}

/**
 * The old `/journeys/people/welcome` and `/journeys/profiles/welcome` URLs.
 * There is no welcome screen, so both spellings forward straight to the People list.
 */
export function JourneysPeopleWelcomeHandoff() {
  return <JourneysHandoff routeKey="people.list" />;
}

/**
 * The `welcome` URL, served at `/people/welcome` and `/profiles/welcome`. There is no welcome
 * screen: it replaces itself with the People list. It takes no `segment`, because the list it
 * leaves for is already the canonical spelling whichever spelling it was reached at.
 */
export function PeopleWelcomeRouteView() {
  const listHref = useMigratedPeopleHref('people.list');

  return <ReplaceNavigate href={listHref} />;
}
